import { IconArrowLeft } from "@tabler/icons-react";
import { useLocale, useTranslations } from "next-intl";
import { ListingItemCateringImage } from "@/components/listing/listing-item-catering-image";
import { Button } from "@/components/ui/button";
import {
  formatCateringName,
  formatDietName,
} from "@/lib/helpers/format-brand-name";
import { useDietDates } from "@/lib/hooks/use-diet-dates";
import { getLocalePrice } from "@/lib/utilities";
import { cn } from "@/lib/utils";
import type { DietCatalogDetail } from "../types";
import { AddDietToCart } from "./diet-add-to-cart";

export const DietDrawerHeader = ({
  data,
  className,
  showCta = true,
  configuration,
  activeMealIngredients,
  activeMealName,
  setActiveMealIngredients,
}: {
  data: DietCatalogDetail;
  className?: string;
  showCta?: boolean;
  configuration?: Record<string, string | number | string[]>;
  activeMealIngredients?: string | null;
  activeMealName?: string | null;
  setActiveMealIngredients?: (mealId: string | null) => void;
}) => {
  const t = useTranslations("diets");
  const locale = useLocale();
  const dietDates = useDietDates({
    diet: data,
    saturdays: true,
    sundays: true,
    locale,
  });

  if (activeMealIngredients) {
    return (
      <header className={cn("relative gap-4", className)}>
        <div className="flex items-center gap-x-3 bg-brand-white px-4 pt-4 pb-4 max-md:justify-center md:mt-0 md:px-3 md:pt-6 md:pr-28">
          <Button
            className="m-0 min-h-0 p-0 max-md:absolute max-md:top-[18px] max-md:left-4"
            onClick={() => setActiveMealIngredients?.(null)}
            variant="ghost"
          >
            <IconArrowLeft className="h-6 w-6" strokeWidth={1.5} />
          </Button>
          <h1 className="font-medium text-lg md:text-2xl">
            <span>{activeMealIngredients}</span>
          </h1>
        </div>
        <div className="bg-brand-grey4 px-4 pt-4 pb-4 font-medium text-2xl text-brand-green1-300 md:mt-0 md:px-12 md:pr-28">
          {activeMealName}
        </div>
      </header>
    );
  }

  return (
    <header
      className={cn(
        "relative mt-[60px] flex items-center gap-4 bg-brand-grey4 px-4 pt-4 pb-4 md:mt-0 md:px-12 md:pt-6 md:pr-28",
        className,
      )}
    >
      <ListingItemCateringImage
        className="hidden md:flex"
        imageUrl={data.catering.logo.url}
        name={formatCateringName(data.catering.name)}
      />
      <div>
        <h1 className="font-medium text-lg md:text-xl">
          <span>{formatDietName(data.name)}</span>
          <span className="mx-2">•</span>
          <span className="whitespace-nowrap">
            {formatCateringName(data.catering.name)}
          </span>
        </h1>
        <div className="flex flex-wrap items-center gap-x-4 text-sm md:mt-1 md:text-base">
          <p className="whitespace-nowrap">
            <span className="mr-1">{t("from")}</span>
            <strong className="mr-1 inline-block font-bold leading-6">
              {getLocalePrice({
                price: Number.parseFloat(data.basePriceNumeric),
              })}
            </strong>
            <span>/ {t("day")}</span>
          </p>
          <p className="whitespace-nowrap">
            <span className="mr-1">{t("available_on")}:</span>
            <span className="inline-block font-bold capitalize">
              {dietDates.nextPossibleDayFormatEeee(new Date())}
            </span>
          </p>
        </div>
        {showCta && (
          <AddDietToCart
            className="xl:h-10"
            configuration={configuration}
            diet={data}
            dietSlug={data.slug}
            kcal={String(data.base.caloricity)}
          />
        )}
      </div>
    </header>
  );
};
